import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { trpc } from "../../lib/trpc";
import { uploadFile } from "../../lib/uploadFile";
import type { CropData } from "../../lib/utils";
import { parseCropData } from "../../lib/utils";
import type { ClubData, Privacy } from "./types";

// Domain rules matching the API's socialUrl() validators
const socialDomains: Record<string, string[]> = {
  discordUrl: ["discord.gg", "discord.com"],
  twitterUrl: ["twitter.com", "x.com"],
  youtubeUrl: ["youtube.com", "youtu.be"],
  facebookUrl: ["facebook.com", "fb.com"],
  twitchUrl: ["twitch.tv"],
  instagramUrl: ["instagram.com"],
  tiktokUrl: ["tiktok.com"],
  kickUrl: ["kick.com"],
};

function validateUrl(value: string, domains?: string[]): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null; // empty is valid (will be cleared)

  // Looks like they typed a handle or slug without the full URL
  if (!trimmed.includes("://") && !trimmed.includes(".")) {
    const hint = domains?.[0] ?? "example.com";
    return `Enter a full URL, e.g. https://${hint}/...`;
  }

  // Missing protocol — they typed "discord.gg/abc" instead of "https://discord.gg/abc"
  if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
    return "Must start with https://";
  }

  try {
    const url = new URL(trimmed);
    if (!["http:", "https:"].includes(url.protocol)) return "Must start with https://";
    if (domains) {
      const allowed = domains.flatMap((d) => [d, `www.${d}`]);
      if (!allowed.includes(url.hostname)) {
        return `Must be a ${domains.join(" or ")} link`;
      }
      // Just the domain with no path isn't a real profile/page link
      if (url.pathname === "/" && !url.search && !url.hash) {
        return `Include the full path, e.g. https://${domains[0]}/...`;
      }
    }
    return null;
  } catch {
    return "This doesn\u2019t look like a valid URL";
  }
}

export type FormErrors = Record<string, string>;

type UploadCategory = "club-avatar" | "club-avatar-source" | "club-banner" | "club-banner-source";

export function useClubSettingsForm(club: ClubData, slug: string) {
  const utils = trpc.useUtils();

  // General fields
  const [name, setName] = useState(club.name);
  const [description, setDescription] = useState(club.description ?? "");
  const [privacy, setPrivacy] = useState<Privacy>(club.privacy);

  // Social links
  const [discordUrl, setDiscordUrl] = useState(club.discordUrl ?? "");
  const [twitterUrl, setTwitterUrl] = useState(club.twitterUrl ?? "");
  const [websiteUrl, setWebsiteUrl] = useState(club.websiteUrl ?? "");
  const [youtubeUrl, setYoutubeUrl] = useState(club.youtubeUrl ?? "");
  const [facebookUrl, setFacebookUrl] = useState(club.facebookUrl ?? "");
  const [twitchUrl, setTwitchUrl] = useState(club.twitchUrl ?? "");
  const [instagramUrl, setInstagramUrl] = useState(club.instagramUrl ?? "");
  const [tiktokUrl, setTiktokUrl] = useState(club.tiktokUrl ?? "");
  const [kickUrl, setKickUrl] = useState(club.kickUrl ?? "");

  // Branding — file inputs
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  // Branding — source files (original uncropped, for upload to *-source category)
  const [pendingAvatarSourceFile, setPendingAvatarSourceFile] = useState<File | null>(null);
  const [pendingBannerSourceFile, setPendingBannerSourceFile] = useState<File | null>(null);

  // Branding — cropped blobs (canvas output, for upload to display category)
  const [pendingAvatarBlob, setPendingAvatarBlob] = useState<Blob | null>(null);
  const [pendingBannerBlob, setPendingBannerBlob] = useState<Blob | null>(null);

  // Branding — preview URLs (blob URLs of cropped version for display in settings)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);

  // Branding — crop data (percentage-based, persisted in DB for restoring crop modal state)
  const [avatarCropData, setAvatarCropData] = useState<CropData | null>(() =>
    parseCropData(club.avatarCropData),
  );
  const [bannerCropData, setBannerCropData] = useState<CropData | null>(() =>
    parseCropData(club.bannerCropData),
  );

  const [isSaving, setIsSaving] = useState(false);

  // Live validation — recomputed on every field change
  const errors = useMemo<FormErrors>(() => {
    const errs: FormErrors = {};
    if (!name.trim()) errs.name = "Name is required";

    const urlFields = [
      ["discordUrl", discordUrl],
      ["twitterUrl", twitterUrl],
      ["websiteUrl", websiteUrl],
      ["youtubeUrl", youtubeUrl],
      ["facebookUrl", facebookUrl],
      ["twitchUrl", twitchUrl],
      ["instagramUrl", instagramUrl],
      ["tiktokUrl", tiktokUrl],
      ["kickUrl", kickUrl],
    ] as const;

    for (const [key, value] of urlFields) {
      const err = validateUrl(value, socialDomains[key]);
      if (err) errs[key] = err;
    }
    return errs;
  }, [
    name,
    discordUrl,
    twitterUrl,
    websiteUrl,
    youtubeUrl,
    facebookUrl,
    twitchUrl,
    instagramUrl,
    tiktokUrl,
    kickUrl,
  ]);

  const hasErrors = Object.keys(errors).length > 0;

  const updateMutation = trpc.club.update.useMutation({
    onSuccess: () => {
      toast.success("Club updated");
      void utils.club.get.invalidate({ slug });
      void utils.club.getMyClubs.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to update club"),
  });

  const doUpload = async (file: Blob | File, category: UploadCategory): Promise<string> => {
    const { publicUrl } = await uploadFile(file, category, club.id);
    if (!publicUrl) throw new Error("Upload did not return a public URL");
    return publicUrl;
  };

  // Server values for comparison (parsed once)
  const serverAvatarCrop = parseCropData(club.avatarCropData);
  const serverBannerCrop = parseCropData(club.bannerCropData);

  const handleSave = async () => {
    if (hasErrors) return;

    setIsSaving(true);
    try {
      const updates: Record<string, unknown> = { clubId: club.id };

      // Only include fields that actually changed
      if (name.trim() !== club.name) updates.name = name.trim();
      const desc = description.trim() || null;
      if (desc !== (club.description ?? null)) updates.description = desc;
      if (privacy !== club.privacy) updates.privacy = privacy;

      // Social links — send null to clear, value to set, omit if unchanged
      const socialFields = [
        ["discordUrl", discordUrl, club.discordUrl],
        ["twitterUrl", twitterUrl, club.twitterUrl],
        ["websiteUrl", websiteUrl, club.websiteUrl],
        ["youtubeUrl", youtubeUrl, club.youtubeUrl],
        ["facebookUrl", facebookUrl, club.facebookUrl],
        ["twitchUrl", twitchUrl, club.twitchUrl],
        ["instagramUrl", instagramUrl, club.instagramUrl],
        ["tiktokUrl", tiktokUrl, club.tiktokUrl],
        ["kickUrl", kickUrl, club.kickUrl],
      ] as const;

      for (const [key, current, server] of socialFields) {
        const val = current.trim() || null;
        if (val !== (server ?? null)) updates[key] = val;
      }

      // Branding — upload source images (original uncropped)
      if (pendingAvatarSourceFile) {
        updates.avatarSourceUrl = await doUpload(pendingAvatarSourceFile, "club-avatar-source");
      }
      if (pendingBannerSourceFile) {
        updates.bannerSourceUrl = await doUpload(pendingBannerSourceFile, "club-banner-source");
      }

      // Branding — upload cropped display images
      if (pendingAvatarBlob) {
        updates.avatarUrl = await doUpload(pendingAvatarBlob, "club-avatar");
      }
      if (pendingBannerBlob) {
        updates.bannerUrl = await doUpload(pendingBannerBlob, "club-banner");
      }

      // Crop data — only send if changed
      if (JSON.stringify(avatarCropData) !== JSON.stringify(serverAvatarCrop)) {
        updates.avatarCropData = avatarCropData;
      }
      if (JSON.stringify(bannerCropData) !== JSON.stringify(serverBannerCrop)) {
        updates.bannerCropData = bannerCropData;
      }

      await updateMutation.mutateAsync(updates as Parameters<typeof updateMutation.mutate>[0]);
      setPendingAvatarSourceFile(null);
      setPendingBannerSourceFile(null);
      setPendingAvatarBlob(null);
      setPendingBannerBlob(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    name.trim() !== club.name ||
    (description.trim() || "") !== (club.description ?? "") ||
    privacy !== club.privacy ||
    (discordUrl.trim() || "") !== (club.discordUrl ?? "") ||
    (twitterUrl.trim() || "") !== (club.twitterUrl ?? "") ||
    (websiteUrl.trim() || "") !== (club.websiteUrl ?? "") ||
    (youtubeUrl.trim() || "") !== (club.youtubeUrl ?? "") ||
    (facebookUrl.trim() || "") !== (club.facebookUrl ?? "") ||
    (twitchUrl.trim() || "") !== (club.twitchUrl ?? "") ||
    (instagramUrl.trim() || "") !== (club.instagramUrl ?? "") ||
    (tiktokUrl.trim() || "") !== (club.tiktokUrl ?? "") ||
    (kickUrl.trim() || "") !== (club.kickUrl ?? "") ||
    !!pendingAvatarBlob ||
    !!pendingBannerBlob ||
    JSON.stringify(avatarCropData) !== JSON.stringify(serverAvatarCrop) ||
    JSON.stringify(bannerCropData) !== JSON.stringify(serverBannerCrop);

  return {
    // General
    name,
    setName,
    description,
    setDescription,
    privacy,
    setPrivacy,
    // Social
    discordUrl,
    setDiscordUrl,
    twitterUrl,
    setTwitterUrl,
    websiteUrl,
    setWebsiteUrl,
    youtubeUrl,
    setYoutubeUrl,
    facebookUrl,
    setFacebookUrl,
    twitchUrl,
    setTwitchUrl,
    instagramUrl,
    setInstagramUrl,
    tiktokUrl,
    setTiktokUrl,
    kickUrl,
    setKickUrl,
    // Branding
    avatarInputRef,
    bannerInputRef,
    avatarPreview,
    bannerPreview,
    setAvatarPreview,
    setBannerPreview,
    pendingAvatarSourceFile,
    setPendingAvatarSourceFile,
    pendingAvatarBlob,
    setPendingAvatarBlob,
    pendingBannerSourceFile,
    setPendingBannerSourceFile,
    pendingBannerBlob,
    setPendingBannerBlob,
    avatarCropData,
    setAvatarCropData,
    bannerCropData,
    setBannerCropData,
    // Validation
    errors,
    hasErrors,
    // Save
    isSaving,
    hasChanges,
    handleSave,
  };
}

export type ClubSettingsForm = ReturnType<typeof useClubSettingsForm>;
